import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Trophy, TrendingUp, TrendingDown, Target, Clock, CheckCircle,
  XCircle, MinusCircle, ChevronRight, Award, BarChart3, Home,
  BookOpen, ArrowUp, ArrowDown, X, Zap, GraduationCap, ArrowRight
} from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '../config';
const API = `${BACKEND_URL}/api`;

// Subject-specific top college cutoffs for CUET
const SUBJECT_COLLEGE_DATA = {
  // ── BCom / BBA track ──────────────────────────────────────────────
  'Accountancy': {
    programs: 'B.Com (Hons)',
    schools: [
      { name: 'SRCC, Delhi University', percentile: 99.8 },
      { name: 'Lady Shri Ram College', percentile: 99.4 },
      { name: 'Hansraj College, DU', percentile: 98.5 },
    ]
  },
  'Business Studies': {
    programs: 'BBA / B.Com (Hons)',
    schools: [
      { name: 'SRCC, Delhi University', percentile: 99.6 },
      { name: 'Lady Shri Ram College', percentile: 99.0 },
      { name: 'Hansraj College, DU', percentile: 98.1 },
    ]
  },
  // ── BA (Hons) track ───────────────────────────────────────────────
  'Economics': {
    programs: 'BA (Hons)',
    schools: [
      { name: "St. Stephen's College, DU", percentile: 99.7 },
      { name: 'SRCC, Delhi University', percentile: 99.6 },
      { name: 'Miranda House, DU', percentile: 99.2 },
    ]
  },
  'English': {
    programs: 'BA (Hons)',
    schools: [
      { name: "St. Stephen's College, DU", percentile: 99.6 },
      { name: 'Miranda House, DU', percentile: 99.3 },
      { name: 'Hindu College, DU', percentile: 98.8 },
    ]
  },
  'History': {
    programs: 'BA (Hons)',
    schools: [
      { name: "St. Stephen's College, DU", percentile: 99.2 },
      { name: 'Hindu College, DU', percentile: 98.7 },
      { name: 'Miranda House, DU', percentile: 98.2 },
    ]
  },
  'Political Science': {
    programs: 'BA (Hons)',
    schools: [
      { name: 'Hindu College, DU', percentile: 99.1 },
      { name: "St. Stephen's College, DU", percentile: 98.8 },
      { name: 'Miranda House, DU', percentile: 98.3 },
    ]
  },
  'Geography': {
    programs: 'BA (Hons)',
    schools: [
      { name: 'Hindu College, DU', percentile: 98.5 },
      { name: 'Miranda House, DU', percentile: 97.9 },
      { name: 'Kamala Nehru College', percentile: 97.0 },
    ]
  },
  'Sociology': {
    programs: 'BA (Hons)',
    schools: [
      { name: 'Lady Shri Ram College', percentile: 98.5 },
      { name: 'Hindu College, DU', percentile: 97.8 },
      { name: 'Miranda House, DU', percentile: 97.2 },
    ]
  },
  'Psychology': {
    programs: 'BA (Hons)',
    schools: [
      { name: 'Lady Shri Ram College', percentile: 99.1 },
      { name: 'Jesus & Mary College', percentile: 98.4 },
      { name: 'Miranda House, DU', percentile: 97.8 },
    ]
  },
  // ── B.Sc (Hons) track ─────────────────────────────────────────────
  'Physics': {
    programs: 'B.Sc',
    schools: [
      { name: 'Hindu College, DU', percentile: 99.2 },
      { name: 'Hansraj College, DU', percentile: 98.5 },
      { name: 'Ramjas College, DU', percentile: 97.8 },
    ]
  },
  'Chemistry': {
    programs: 'B.Sc',
    schools: [
      { name: 'Hindu College, DU', percentile: 98.8 },
      { name: 'Hansraj College, DU', percentile: 98.1 },
      { name: 'Miranda House, DU', percentile: 97.5 },
    ]
  },
  'Mathematics': {
    programs: 'B.Sc',
    schools: [
      { name: 'Hindu College, DU', percentile: 99.3 },
      { name: 'Hansraj College, DU', percentile: 98.6 },
      { name: 'Ramjas College, DU', percentile: 98.0 },
    ]
  },
  'Biology': {
    programs: 'B.Sc',
    schools: [
      { name: 'Miranda House, DU', percentile: 98.9 },
      { name: 'Hindu College, DU', percentile: 98.3 },
      { name: 'Gargi College, DU', percentile: 97.1 },
    ]
  },
  'Computer Science': {
    programs: 'B.Sc',
    schools: [
      { name: 'Hindu College, DU', percentile: 99.0 },
      { name: 'Hansraj College, DU', percentile: 98.4 },
      { name: 'Ramjas College, DU', percentile: 97.5 },
    ]
  },
};
const DEFAULT_COLLEGE_DATA = {
  programs: 'your chosen programme',
  schools: [
    { name: 'Top DU Colleges', percentile: 99.0 },
    { name: 'Central Universities', percentile: 97.5 },
    { name: 'Top State Universities', percentile: 95.0 },
  ]
};

// Upgrade prompt modal shown to free users after completing a mock
function UpgradePromptModal({ subject, currentPercentile, currentScore, onClose }) {
  const navigate = useNavigate();
  const data = SUBJECT_COLLEGE_DATA[subject] || DEFAULT_COLLEGE_DATA;
  const lowestCutoff = data.schools[data.schools.length - 1].percentile;
  const topCutoff = data.schools[0].percentile;
  const gap = Math.max(0, lowestCutoff - currentPercentile).toFixed(1);
  const alreadyQualifies = currentPercentile >= lowestCutoff;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}
      data-testid="upgrade-prompt-modal"
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        {/* Top accent bar */}
        <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #4f46e5, #7c3aed, #db2777)' }} />

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="upgrade-modal-close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-2 mb-1">
            <GraduationCap className="h-5 w-5 text-indigo-600" />
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">
              {data.programs} · Top Colleges
            </p>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-4 leading-snug">
            {alreadyQualifies
              ? `You're on track! Keep it up.`
              : `You're ${gap} percentile points away from your dream college`}
          </h2>

          {/* Current vs Target */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-500">Your percentile</span>
              <span className="text-sm text-gray-500">Target ({data.schools[0].name.split(',')[0]})</span>
            </div>
            <div className="flex justify-between items-end mb-2">
              <span className={`text-2xl font-extrabold ${alreadyQualifies ? 'text-green-600' : 'text-red-500'}`}>
                {currentPercentile.toFixed(1)}%
              </span>
              <span className="text-2xl font-extrabold text-indigo-600">{topCutoff}%</span>
            </div>
            {/* Gap bar */}
            <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(100, (currentPercentile / topCutoff) * 100)}%`,
                  background: alreadyQualifies
                    ? 'linear-gradient(90deg, #10b981, #059669)'
                    : 'linear-gradient(90deg, #ef4444, #f97316)',
                }}
              />
              {/* Target marker */}
              <div className="absolute top-0 h-full w-0.5 bg-indigo-600" style={{ left: '100%', marginLeft: '-1px' }} />
            </div>
            {!alreadyQualifies && (
              <p className="text-xs text-gray-500 mt-2">
                Score {Math.round((topCutoff - currentPercentile) / 100 * 101)} more ranks higher to reach the top school cutoff
              </p>
            )}
          </div>

          {/* College cutoffs */}
          <div className="space-y-2 mb-5">
            {data.schools.map((school, i) => {
              const meetsThis = currentPercentile >= school.percentile;
              return (
                <div
                  key={i}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    meetsThis ? 'border-green-200 bg-green-50' : 'border-gray-100 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {meetsThis
                      ? <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                      : <div className="h-4 w-4 rounded-full border-2 border-gray-300 shrink-0" />}
                    <span className="text-sm font-medium text-gray-800">{school.name}</span>
                  </div>
                  <span className={`text-sm font-bold shrink-0 ml-2 ${meetsThis ? 'text-green-600' : 'text-gray-500'}`}>
                    {school.percentile}%ile+
                  </span>
                </div>
              );
            })}
          </div>

          {/* Weak areas callout */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 flex gap-2">
            <Zap className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">
              <strong>Pro plan</strong> gives you unlimited mocks, detailed topic-wise analysis, and weak-area drills — exactly what closes the gap to {data.schools[0].name.split(',')[0]}.
            </p>
          </div>

          {/* CTAs */}
          <div className="space-y-2">
            <Button
              onClick={() => navigate('/upgrade')}
              className="w-full text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(90deg, #4f46e5, #7c3aed)' }}
              data-testid="upgrade-to-pro-btn"
            >
              <Zap className="h-4 w-4" />
              Upgrade to Pro — Practice More, Rank Higher
              <ArrowRight className="h-4 w-4" />
            </Button>
            <button
              onClick={onClose}
              className="w-full text-sm text-gray-400 hover:text-gray-600 py-2 transition-colors"
              data-testid="upgrade-modal-dismiss"
            >
              No, I don't want a better percentile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Render question text with proper formatting (assertion-reason, formulas, multiline)
const renderResultQuestion = (text) => {
  if (!text) return null;
  const cleaned = text.trim();

  // Match-the-following: just render multiline
  const hasAssertion = /assertion\s*\(?[Aa]\)?/i.test(cleaned);
  const hasReason = /reason\s*\(?[Rr]\)?/i.test(cleaned);

  if (hasAssertion && hasReason) {
    const assertMatch = cleaned.match(/assertion\s*\(?[Aa]\)?[:\s]*([\s\S]+?)(?=reason\s*\(?[Rr]\)?)/i);
    const reasonMatch = cleaned.match(/reason\s*\(?[Rr]\)?[:\s]*([\s\S]+?)$/i);
    const preambleMatch = cleaned.match(/^([\s\S]+?)(?=assertion\s*\(?[Aa]\)?)/i);
    const preamble = preambleMatch ? preambleMatch[1].trim() : '';
    return (
      <div className="space-y-2">
        {preamble && <p className="font-medium text-primary">{preamble}</p>}
        {assertMatch && (
          <div className="p-2 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
            <p className="text-xs font-bold text-blue-600 uppercase mb-1">Assertion (A)</p>
            <p className="text-sm text-gray-800">{assertMatch[1].trim()}</p>
          </div>
        )}
        {reasonMatch && (
          <div className="p-2 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
            <p className="text-xs font-bold text-amber-700 uppercase mb-1">Reason (R)</p>
            <p className="text-sm text-gray-800">{reasonMatch[1].trim()}</p>
          </div>
        )}
      </div>
    );
  }

  const lines = cleaned.split('\n').filter(l => l.trim());
  if (lines.length <= 1) {
    return <p className="font-medium text-primary">{cleaned}</p>;
  }
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        const isFormula = /[=≠≤≥±×÷∝∞∫√²³⁰¹⁴⁵⁶⁷⁸⁹₀₁₂₃₄₅₆₇₈₉]/.test(line) &&
          line.trim().length < 80 &&
          !/\b(is|are|was|were|the|and|for|that|this|with)\b/i.test(line);
        if (isFormula) {
          return <div key={i} className="px-3 py-1 bg-slate-100 border border-slate-200 rounded font-mono text-sm text-slate-800">{line.trim()}</div>;
        }
        return <p key={i} className="font-medium text-primary leading-relaxed">{line.trim()}</p>;
      })}
    </div>
  );
};

export default function ResultsPage() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();

  const [result, setResult] = useState(location.state?.result || null);
  const [analytics, setAnalytics] = useState(null);
  const [solutions, setSolutions] = useState(null);
  const [loading, setLoading] = useState(!location.state?.result);
  const [showRankReveal, setShowRankReveal] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    if (!result) {
      fetchAnalytics();
    } else {
      // Show rank reveal animation then confetti
      setTimeout(() => {
        setShowRankReveal(false);
        if (result.percentile >= 50) setShowConfetti(true);
        // Show upgrade modal for free users after rank reveal (1s delay)
        if (user?.plan === 'free' || !user?.plan) {
          setTimeout(() => setShowUpgradeModal(true), 1000);
        }
      }, 3000);
      if (result.percentile >= 50) setTimeout(() => setShowConfetti(false), 8000);
      fetchAnalytics();
    }
  }, [attemptId]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/attempt/${attemptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setAnalytics(response.data.data);
        if (!result) {
          setResult({
            subject: response.data.data.subject,
            score: response.data.data.score,
            rank: response.data.data.rank,
            percentile: response.data.data.percentile,
            correct_count: response.data.data.correct_count,
            incorrect_count: response.data.data.incorrect_count,
            unattempted_count: response.data.data.unattempted_count,
            time_taken_seconds: response.data.data.total_time_seconds
          });
          // Show upgrade modal for free users (loaded from API path)
          if (user?.plan === 'free' || !user?.plan) {
            setTimeout(() => setShowUpgradeModal(true), 1000);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
      setTimeout(() => setShowRankReveal(false), 3000);
    }
  };

  const fetchSolutions = async () => {
    if (solutions) return;
    try {
      const response = await axios.get(`${API}/analytics/solutions/${attemptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSolutions(response.data.data.solutions);
      }
    } catch (error) {
      console.error('Error fetching solutions:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-section flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cta border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading results...</p>
        </div>
      </div>
    );
  }

  // Rank Reveal Animation
  if (showRankReveal && result) {
    const pctMsg = result.percentile >= 90 ? "Outstanding!" :
                   result.percentile >= 70 ? "Great job!" :
                   result.percentile >= 50 ? "Good effort!" : "Keep pushing!";
    return (
      <div className="min-h-screen bg-brand flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-pulse">
            <Trophy className="h-24 w-24 text-amber-400 mx-auto mb-6" />
            <h1 className="text-3xl font-bold mb-2">Calculating Your Rank...</h1>
            <div className="text-7xl font-extrabold text-amber-400 animate-bounce my-6">
              #{result.rank}
            </div>
            <p className="text-2xl font-semibold text-amber-300 mb-2">{pctMsg}</p>
            <p className="text-lg text-white/70">
              {result.percentile.toFixed(1)}th Percentile &middot; {result.score}/250
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-section">
      {/* Upgrade Prompt Modal — free users only */}
      {showUpgradeModal && result && (
        <UpgradePromptModal
          subject={result.subject}
          currentPercentile={result.percentile}
          currentScore={result.score}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}

      {/* Confetti Effect */}
      {showConfetti && (
        <div className="fixed inset-0 z-50 pointer-events-none overflow-hidden" data-testid="confetti-overlay">
          {[...Array(60)].map((_, i) => (
            <div key={i} className="absolute animate-bounce" style={{
              left: `${Math.random() * 100}%`,
              top: `-${Math.random() * 20}px`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 3}s`,
              width: `${6 + Math.random() * 8}px`,
              height: `${6 + Math.random() * 8}px`,
              borderRadius: Math.random() > 0.5 ? '50%' : '2px',
              backgroundColor: ['#f59e0b','#3b82f6','#10b981','#ef4444','#8b5cf6','#ec4899'][Math.floor(Math.random()*6)],
              opacity: 0.8,
              transform: `rotate(${Math.random()*360}deg)`,
            }} />
          ))}
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center">
              <img src="/logo-full.png" alt="Crack CUET" className="h-16 md:h-20" />
            </Link>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              <Home className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Score Summary */}
        <Card className="border-0 shadow-xl mb-8 overflow-hidden">
          <div className="bg-brand p-8">
            <div className="grid md:grid-cols-4 gap-6 text-white text-center">
              <div>
                <p className="text-white/60 text-sm mb-1">Your Score</p>
                <p className="text-4xl font-extrabold">{result.score}/250</p>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">National Rank</p>
                <p className="text-4xl font-extrabold text-amber-400">#{result.rank}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">Percentile</p>
                <p className="text-4xl font-extrabold text-green-400">{result.percentile.toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">Time Taken</p>
                <p className="text-4xl font-extrabold">{formatTime(result.time_taken_seconds)}</p>
              </div>
            </div>
          </div>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-8">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-green-600 font-medium">{result.correct_count} Correct</span>
                </div>
                <div className="flex items-center space-x-2">
                  <XCircle className="h-5 w-5 text-red-500" />
                  <span className="text-red-600 font-medium">{result.incorrect_count} Incorrect</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MinusCircle className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-600 font-medium">{result.unattempted_count} Unattempted</span>
                </div>
              </div>
              
              {/* Badges Earned */}
              {result.badges_earned && result.badges_earned.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Award className="h-5 w-5 text-amber-500" />
                  {result.badges_earned.map((badge, idx) => (
                    <Badge key={idx} className="bg-amber-100 text-amber-700 border-0">
                      {badge}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Percentile Message */}
            {result.percentile_message && (
              <div className="mt-4 p-4 bg-cta/10 rounded-xl">
                <p className="text-cta font-medium text-center">{result.percentile_message}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs for Analytics and Solutions */}
        <Tabs defaultValue="analytics" className="space-y-6" onValueChange={(v) => v === 'solutions' && fetchSolutions()}>
          <TabsList className="bg-white shadow-sm border">
            <TabsTrigger value="analytics" className="data-[state=active]:bg-cta data-[state=active]:text-white">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="solutions" className="data-[state=active]:bg-cta data-[state=active]:text-white">
              <BookOpen className="h-4 w-4 mr-2" />
              Solutions
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {analytics && (
              <>
                {/* Topic Breakdown */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle>Topic-wise Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {analytics.topic_breakdown.map((topic, idx) => (
                        <div key={idx} className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-primary">{topic.topic}</span>
                            <div className="flex items-center space-x-4 text-sm">
                              <span className="text-green-600">{topic.correct} correct</span>
                              <span className="text-red-600">{topic.incorrect} wrong</span>
                              <span className="text-gray-500">{topic.unattempted} skipped</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <Progress value={topic.accuracy} className="flex-1 h-2" />
                            <span className={`font-bold ${
                              topic.accuracy >= 70 ? 'text-green-600' :
                              topic.accuracy >= 50 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {topic.accuracy.toFixed(0)}%
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Avg time: {topic.avg_time_seconds.toFixed(0)}s per question
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Strengths and Weaknesses */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center text-green-600">
                        <TrendingUp className="h-5 w-5 mr-2" />
                        Your Strengths
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.strengths.length > 0 ? (
                        <ul className="space-y-2">
                          {analytics.strengths.map((topic, idx) => (
                            <li key={idx} className="flex items-center space-x-2 p-2 bg-green-50 rounded-lg">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-green-700">{topic}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-500">Keep practicing to identify your strong areas!</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center text-red-600">
                        <TrendingDown className="h-5 w-5 mr-2" />
                        Areas to Improve
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.weaknesses.length > 0 ? (
                        <ul className="space-y-2">
                          {analytics.weaknesses.map((topic, idx) => (
                            <li key={idx} className="flex items-center space-x-2 p-2 bg-red-50 rounded-lg">
                              <Target className="h-4 w-4 text-red-500" />
                              <span className="text-red-700">{topic}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-500">Great job! No weak areas identified.</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Solutions Tab */}
          <TabsContent value="solutions">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Answer Key & Solutions</CardTitle>
              </CardHeader>
              <CardContent>
                {solutions ? (
                  <div className="space-y-6">
                    {solutions.map((sol, idx) => (
                      <div key={idx} className={`p-4 rounded-xl border-2 ${
                        sol.is_correct === true ? 'border-green-200 bg-green-50' :
                        sol.is_correct === false ? 'border-red-200 bg-red-50' :
                        'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-center justify-between mb-3">
                          <Badge className={`border-0 ${
                            sol.is_correct === true ? 'bg-green-200 text-green-700' :
                            sol.is_correct === false ? 'bg-red-200 text-red-700' :
                            'bg-gray-200 text-gray-700'
                          }`}>
                            Q{idx + 1} - {sol.is_correct === true ? 'Correct' : sol.is_correct === false ? 'Incorrect' : 'Unattempted'}
                          </Badge>
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <span>{sol.topic}</span>
                            <Badge variant="outline" className="capitalize">{sol.difficulty}</Badge>
                          </div>
                        </div>

                        {sol.diagram_ascii && (() => {
                          const ascii = sol.diagram_ascii;
                          const isOrbital = /\[[\s↑↓^]*\]/.test(ascii) && ascii.trim().length < 200;
                          const drawingRatio = (ascii.match(/[|\-=+\\\/^<>~_]/g) || []).length / Math.max(ascii.replace(/\s/g,'').length, 1);
                          const isUseful = ascii.trim().length >= 10 && !(drawingRatio > 0.70 && ascii.split('\n').filter(l=>l.trim()).length < 4);
                          if (!isUseful && !isOrbital) return null;
                          return (
                            <div className="mb-3 rounded-lg border border-gray-200 bg-slate-50 overflow-x-auto">
                              <div className="px-3 py-1 border-b border-gray-200">
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                  {isOrbital ? 'Electron Configuration' : 'Reference Diagram'}
                                </span>
                              </div>
                              <div className="p-3">
                                <pre className="text-sm text-gray-800 whitespace-pre leading-relaxed font-mono overflow-x-auto">{ascii}</pre>
                              </div>
                            </div>
                          );
                        })()}

                        <div className="mb-4">
                          {renderResultQuestion(sol.question_text)}
                        </div>

                        <div className="space-y-2 mb-4">
                          {sol.options.map((opt, optIdx) => (
                            <div
                              key={opt.id}
                              className={`p-3 rounded-lg flex items-center space-x-3 ${
                                opt.is_correct ? 'bg-green-100 border border-green-300' :
                                sol.selected_option_id === opt.id ? 'bg-red-100 border border-red-300' :
                                'bg-white border border-gray-200'
                              }`}
                            >
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                opt.is_correct ? 'bg-green-500 text-white' :
                                sol.selected_option_id === opt.id ? 'bg-red-500 text-white' :
                                'bg-gray-200 text-gray-600'
                              }`}>
                                {String.fromCharCode(65 + optIdx)}
                              </span>
                              <span className={`flex-1 ${
                                opt.is_correct ? 'text-green-700 font-medium' :
                                sol.selected_option_id === opt.id ? 'text-red-700' :
                                'text-gray-700'
                              }`}>
                                {opt.text}
                              </span>
                              {opt.is_correct && <CheckCircle className="h-5 w-5 text-green-500" />}
                              {sol.selected_option_id === opt.id && !opt.is_correct && <XCircle className="h-5 w-5 text-red-500" />}
                            </div>
                          ))}
                        </div>

                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm font-medium text-blue-700 mb-1">Explanation:</p>
                          <p className="text-sm text-blue-800">{sol.explanation}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 border-4 border-cta border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading solutions...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8" data-testid="results-actions">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="px-8"
            data-testid="back-to-dashboard-btn"
          >
            <Home className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <Button
            onClick={() => navigate(`/leaderboard/${encodeURIComponent(analytics?.subject || result?.subject || 'Physics')}`)}
            className="bg-cta hover:bg-cta-hover text-white px-8"
            data-testid="view-leaderboard-btn"
          >
            <Trophy className="h-4 w-4 mr-2" />
            View Leaderboard
          </Button>
        </div>
      </main>
    </div>
  );
}
